/* OHAS Admin Panel — certify a bookstore book at D0-D4, view the
 * latest manifest, and record an editor D3 sign-off suggestion.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { ShieldCheck, Award, RefreshCw, FileText, Loader2, Copy } from 'lucide-react';
import { openConfirm } from '@/components/admin/ConfirmDialog';

const API = (process.env.REACT_APP_BACKEND_URL || '') + '/api';
const http = axios.create({ baseURL: API, withCredentials: true });

const LEVEL_TONES = {
  D0: 'text-slate-500',
  D1: 'text-blue-500',
  D2: 'text-emerald-500',
  D3: 'text-purple-500',
  D4: 'text-amber-500',
};

export default function OhasCertifyPanel() {
  const [books, setBooks] = useState([]);
  const [active, setActive] = useState(null);
  const [manifest, setManifest] = useState(null);
  const [busy, setBusy] = useState(false);

  const loadBooks = async () => {
    try {
      const r = await http.get('/admin/bookstore/books');
      setBooks(r.data?.books || []);
      if (!active && r.data?.books?.length) setActive(r.data.books[0].id);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to load books.');
    }
  };
  useEffect(() => {
    loadBooks();
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, []);

  const loadManifest = async (bookId) => {
    if (!bookId) return;
    setBusy(true);
    setManifest(null);
    try {
      const r = await http.get(`/admin/ohas/${bookId}/manifest`);
      setManifest(r.data.manifest);
    } catch (err) {
      if (err?.response?.status === 404) setManifest(null);
      else toast.error(err?.response?.data?.detail || 'Manifest load failed.');
    } finally { setBusy(false); }
  };
  useEffect(() => { if (active) loadManifest(active); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [active]);

  const certify = async () => {
    if (!active) return;
    setBusy(true);
    try {
      const r = await http.post(`/admin/ohas/${active}/certify`, {});
      setManifest(r.data.manifest);
      toast.success(`Certified at ${r.data.manifest.level}.`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Certify failed.');
    } finally { setBusy(false); }
  };

  const copyManifest = () => {
    if (!manifest) return;
    navigator.clipboard.writeText(JSON.stringify(manifest, null, 2));
    toast.success('Manifest JSON copied.');
  };

  return (
    <div className="space-y-4" data-testid="admin-ohas-panel">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="flex items-center gap-2 text-[16px] font-semibold tracking-tight">
            <ShieldCheck className="h-4 w-4 text-primary" /> OHAS Certification
          </h2>
          <p className="mt-0.5 text-[11.5px] text-muted-foreground">
            Cryptographically prove human authorship. Certify any book at D0–D4.
            The manifest is publicly verifiable at <code className="text-primary">/api/ohas/&lt;book_id&gt;/manifest.json</code>.
          </p>
        </div>
        <button type="button" onClick={loadBooks} data-testid="ohas-refresh"
                className="inline-flex items-center gap-1.5 rounded-sm border border-border bg-background px-2.5 py-1.5 text-[11.5px] hover:bg-secondary">
          <RefreshCw className="h-3 w-3" /> Refresh
        </button>
      </div>

      <div className="grid gap-4 md:grid-cols-[280px_1fr]">
        <aside className="max-h-[600px] overflow-y-auto rounded-md border border-border bg-card p-2" data-testid="ohas-book-list">
          {books.length === 0 && (
            <div className="p-3 text-[12px] text-muted-foreground">No bookstore books available.</div>
          )}
          {books.map((b) => (
            <button
              key={b.id}
              type="button"
              onClick={() => setActive(b.id)}
              data-testid={`ohas-book-${b.id}`}
              className={`block w-full rounded-sm border px-2 py-1.5 text-left text-[12px] transition ${
                active === b.id ? 'border-primary bg-primary/10' : 'border-transparent hover:bg-secondary'
              }`}
            >
              <div className="truncate font-semibold">{b.title}</div>
              <div className="text-[10.5px] text-muted-foreground">{b.author || '—'}</div>
            </button>
          ))}
        </aside>

        <section className="rounded-md border border-border bg-card p-4">
          {!active ? (
            <div className="text-[12px] text-muted-foreground">Select a book to view its OHAS certification.</div>
          ) : busy ? (
            <div className="flex items-center gap-2 text-[12px] text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading manifest…
            </div>
          ) : manifest ? (
            <div className="space-y-3">
              <div className="flex items-center gap-3">
                <Award className={`h-8 w-8 ${LEVEL_TONES[manifest.level] || 'text-slate-500'}`} />
                <div>
                  <div className="text-[11px] uppercase tracking-wider text-muted-foreground">Current level</div>
                  <div className="text-[20px] font-semibold">{manifest.level} · {manifest.level_label}</div>
                </div>
                <div className="ml-auto flex gap-2">
                  <a
                    href={`${API}/ohas/${active}/badge.svg`}
                    target="_blank" rel="noreferrer"
                    className="inline-flex items-center gap-1 rounded-sm border border-border bg-background px-2 py-1 text-[11.5px] hover:bg-secondary"
                    data-testid="ohas-badge-link"
                  >
                    <FileText className="h-3 w-3" /> Badge
                  </a>
                  <a
                    href={`${API}/ohas/${active}/manifest.json`}
                    target="_blank" rel="noreferrer"
                    className="inline-flex items-center gap-1 rounded-sm border border-border bg-background px-2 py-1 text-[11.5px] hover:bg-secondary"
                    data-testid="ohas-manifest-link"
                  >
                    <FileText className="h-3 w-3" /> Manifest
                  </a>
                  <button
                    type="button"
                    onClick={copyManifest}
                    className="inline-flex items-center gap-1 rounded-sm border border-border bg-background px-2 py-1 text-[11.5px] hover:bg-secondary"
                    data-testid="ohas-manifest-copy"
                  >
                    <Copy className="h-3 w-3" /> Copy JSON
                  </button>
                </div>
              </div>
              <div className="rounded-sm border border-border bg-panel-header/40 p-3 text-[12px]">
                <div><strong>Certification ID:</strong> {manifest.certification_id}</div>
                <div><strong>Issued:</strong> {manifest.issued_at}</div>
                <div><strong>Manifest SHA-256:</strong> <code className="text-[10.5px]">{manifest.manifest_sha256}</code></div>
                <div className="mt-2">
                  <strong>Reasons:</strong>
                  <ul className="mt-1 list-disc pl-4">
                    {manifest.reasons.map((r, i) => <li key={i}>{r}</li>)}
                  </ul>
                </div>
              </div>
              <img
                src={`${API}/ohas/${active}/qr.png`}
                alt="OHAS verification QR"
                className="h-40 w-40 rounded border border-border bg-white p-1"
                data-testid="ohas-qr-preview"
              />
            </div>
          ) : (
            <div className="text-[12px] text-muted-foreground">
              This book has not been certified yet. Click the button below to
              build a fresh manifest from the current evidence.
            </div>
          )}
          <div className="mt-4">
            <button
              type="button"
              onClick={certify}
              disabled={busy || !active}
              data-testid="ohas-certify-button"
              className="inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-[12.5px] font-semibold text-primary-foreground hover:bg-primary/90 disabled:opacity-60"
            >
              <ShieldCheck className="h-4 w-4" /> {manifest ? 'Re-certify' : 'Certify now'}
            </button>
            <button
              type="button"
              onClick={async () => {
                if (!(await openConfirm({
                  title: 'Certify every published book at once?',
                  confirmLabel: 'Certify all',
                }))) return;
                try {
                  const r = await http.post('/admin/ohas/bulk-certify');
                  toast.success(`Bulk certify done: ${r.data.success}/${r.data.total} — ${JSON.stringify(r.data.by_level)}`);
                  loadBooks();
                } catch (err) { toast.error(err?.response?.data?.detail || 'Bulk certify failed.'); }
              }}
              data-testid="ohas-bulk-certify-button"
              className="ml-2 inline-flex items-center gap-2 rounded-md border border-primary/40 bg-primary/10 px-4 py-2 text-[12.5px] font-semibold text-primary hover:bg-primary/20"
            >
              <Award className="h-4 w-4" /> Bulk certify every book
            </button>
            <button
              type="button"
              onClick={async () => {
                try {
                  const r = await http.post('/admin/wipo/contracts/tick-reminders?dry_run=false');
                  toast.success(`WIPO reminders sent: ${r.data.count}`);
                } catch (err) { toast.error(err?.response?.data?.detail || 'Tick failed.'); }
              }}
              data-testid="wipo-tick-reminders-button"
              className="ml-2 inline-flex items-center gap-2 rounded-md border border-border bg-background px-4 py-2 text-[12.5px] font-semibold hover:bg-secondary"
            >
              <FileText className="h-4 w-4" /> Ping stale WIPO contracts
            </button>
          </div>
        </section>
      </div>
    </div>
  );
}
